/**
 * Flowto.AI 공통 고객센터 설정 (프로젝트별)
 *
 * 규칙: 모든 프로젝트의 고객센터는 flowto-ai.com/support/<project_name> 에 위치한다.
 * 새 프로젝트를 추가하려면 이 맵에 slug 키로 엔트리를 하나 추가하면 된다.
 * 페이지 렌더는 src/pages/Support.js (공통 템플릿) 가 담당한다.
 *
 * 이용약관(termsUrl)·개인정보처리방침(privacyUrl)은 기본 포함 항목이다.
 *
 * @typedef {{ q: string, a: string }} Faq
 * @typedef {{ label: string, url: string }} PolicyLink
 * @typedef {{ steps: string[], deleted: string, retained: string, retentionPeriod: string }} DeletionFlow
 * @typedef {{ developerName: string, account: DeletionFlow, data: DeletionFlow }} DataDeletion
 * @typedef {{
 *   serviceName: string,
 *   serviceNameKo: string,
 *   heroSubtitle: string,
 *   faq: Faq[],
 *   contactEmail: string,
 *   inquiryCategories: string[],
 *   termsUrl: string,
 *   privacyUrl: string,
 *   extraPolicies?: PolicyLink[],
 *   dataDeletion?: DataDeletion,
 *   webhookSource: string,
 * }} SupportConfig
 */

/** @type {Record<string, SupportConfig>} */
export const supportConfig = {
  meetrip: {
    serviceName: 'Meetrip',
    serviceNameKo: '밋트립',
    heroSubtitle:
      '자주 묻는 질문을 먼저 확인해 보시고, 해결되지 않는 내용은 아래 문의 폼으로 보내 주세요. 영업일 기준 1~2일 안에 답변드립니다.',
    contactEmail: 'flowto.ai.master@gmail.com',
    inquiryCategories: [
      '일반 문의',
      '신고·안전',
      '계정·로그인',
      '데이터·개인정보',
      '기타',
    ],
    // 약관·정책 본문은 Supabase(legal_policies, app='meetrip')에서 불러와
    // 홈페이지 내부 페이지로 표시한다. (SupportLegal.js)
    termsUrl: '/support/meetrip/terms',
    privacyUrl: '/support/meetrip/privacy',
    extraPolicies: [
      {
        label: '아동 안전 표준',
        url: 'https://meetrip-simple-api.flowto-ai.com/policies/child-safety',
      },
    ],
    // Google Play 데이터 삭제 요건용 — 스토어 등록정보에 이 페이지 URL 을 등록한다.
    //   데이터 삭제:  https://flowto-ai.com/support/meetrip#delete-data
    //   계정 삭제:    https://flowto-ai.com/support/meetrip#delete-account
    // 각 항목은 앱/개발자명, 요청 단계, 삭제·보관 데이터 유형과 보관기간을 명시한다.
    dataDeletion: {
      developerName: 'Flowto.AI',
      account: {
        steps: [
          '이 페이지 하단 “문의 보내기”에서 문의 분류를 “계정·로그인”으로 선택합니다. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
          '문의 내용에 “계정 삭제(탈퇴) 요청”과 가입에 사용한 이메일(또는 앱에서 접속 시 자동 입력되는 고객 ID)을 적어 보냅니다.',
          '본인 확인 후 영업일 기준 1~2일 안에 계정과 관련 데이터를 삭제하고, 처리 완료를 회신드립니다.',
        ],
        deleted:
          '프로필(닉네임·성별·출생연도·프로필 사진·소개), 이메일 및 계정 식별자(user ID), 채팅 메시지, 매칭·여행 일정·미팅 기록, 위치 기록, 앱 활동 로그',
        retained: '신고·차단 등 분쟁 해결과 관련된 최소한의 기록',
        retentionPeriod:
          '관련 법령이 허용·요구하는 범위에서 최대 1년간 보관 후 영구 파기 (그 외 데이터는 요청 처리 시 즉시 파기)',
      },
      data: {
        steps: [
          '이 페이지 하단 “문의 보내기”에서 문의 분류를 “데이터·개인정보”로 선택합니다. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
          '삭제를 원하는 데이터 항목(예: 프로필 사진, 특정 채팅 내역, 위치 기록 등)과 가입 이메일을 적어 보냅니다.',
          '본인 확인 후 영업일 기준 1~2일 안에 해당 데이터를 삭제하고, 처리 완료를 회신드립니다.',
        ],
        deleted: '요청하신 특정 데이터 항목 (프로필 사진, 채팅 내역, 위치 기록, 관심사 등)',
        retained: '전자상거래·통신 관련 법령상 보관 의무가 있는 정보',
        retentionPeriod: '법정 보관 의무 기간이 지난 뒤 파기 (의무 없는 데이터는 즉시 파기)',
      },
    },
    webhookSource: 'support-meetrip',
    faq: [
      {
        q: '밋트립은 어떤 서비스인가요?',
        a: '밋트립은 같은 시기에 일본을 여행하는 사람들을 일정·관심사·여행 스타일로 이어주는 여행 동행 매칭 앱입니다. 취향이 맞는 사람과 부담 없이, 안전하게 만나 점심 한 끼·카페 1시간·야경 30분 같은 짧은 만남을 즐길 수 있어요.',
      },
      {
        q: '매칭이 잘 안 돼요.',
        a: '먼저 여행 일정(도시·날짜)이 등록되어 있어야 후보가 조회됩니다. 관심사와 여행 스타일을 채우면 매칭 정확도가 올라가요. 같은 시기·도시를 여행하는 사용자가 적은 시기에는 후보가 적게 보일 수 있습니다.',
      },
      {
        q: '안전 가입(안전 확인)은 왜 해야 하나요?',
        a: '밋트립은 낯선 사람과 실제로 만나는 서비스이기 때문에, 매칭 기능을 사용하기 전 안전 체크리스트 확인과 실명·정확 정보 약속이 필요합니다. 최초 1회만 진행하면 되고, 이후에는 다시 요구하지 않습니다.',
      },
      {
        q: '부적절한 사용자를 신고하거나 차단하려면 어떻게 하나요?',
        a: '상대 프로필·채팅 화면의 신고 버튼으로 부적절한 사용자·메시지를 바로 신고할 수 있고, 차단하면 매칭·연락이 즉시 중단됩니다. 아동 안전 관련 신고는 아동 안전 표준에 따라 최우선으로 처리합니다. 범죄 피해가 의심되면 현지 경찰(일본 110) 또는 영사콜센터(+82-2-3210-0404)에 먼저 연락하세요.',
      },
      {
        q: '상대가 약속에 나오지 않았어요 (노쇼).',
        a: '노쇼(약속 불이행) 이용자는 일정 기간 매칭이 제한되며 신고가 누적됩니다. 채팅방에서 신고하거나 아래 문의 폼(분류 “신고·안전”)으로 알려주시면 확인 후 조치합니다.',
      },
      {
        q: '만 18세 미만도 이용할 수 있나요?',
        a: '아니요. 밋트립은 성인 간의 여행 동행 매칭을 위한 서비스로, 만 18세 이상만 이용할 수 있습니다. 미성년자로 확인되거나 합리적으로 의심되는 계정은 이용이 제한되며, 확인 절차 후 삭제됩니다. 자세한 내용은 아동 안전 표준을 참고하세요.',
      },
      {
        q: '계정을 삭제(탈퇴)하려면 어떻게 하나요?',
        a: '이 페이지의 “데이터 및 계정 삭제 요청” 섹션(계정 및 관련 데이터 삭제)에서 단계·삭제되는 데이터·보관 기간을 확인하고 요청하실 수 있습니다. 간단히는, 아래 “문의 보내기”에서 분류 “계정·로그인”을 선택해 탈퇴를 요청하시면 영업일 기준 1~2일 안에 계정과 관련 데이터를 영구 삭제해 드립니다.',
      },
      {
        q: '내 개인정보는 어떻게 보호되나요? 어떤 데이터를 수집하나요?',
        a: '밋트립은 서비스 제공에 필요한 최소한의 정보(닉네임·성별·출생연도 등 프로필, 이메일, 위치, 채팅 내용, 기기 정보)를 수집합니다. 모든 데이터는 암호화된 채널로 전송·저장되며, 본인 계정에서만 접근할 수 있습니다. 수집·이용·보관에 대한 자세한 내용은 개인정보처리방침을 확인해 주세요.',
      },
      {
        q: '내 데이터 삭제를 요청하려면 어떻게 하나요?',
        a: '계정 탈퇴와 별개로 특정 데이터만 삭제하고 싶으시면, 이 페이지의 “데이터 및 계정 삭제 요청” 섹션(특정 데이터 삭제)에서 단계·삭제되는 데이터·보관 기간을 확인하실 수 있습니다. 간단히는, 아래 “문의 보내기”에서 분류 “데이터·개인정보”를 선택해 삭제할 항목을 알려주시면 처리해 드립니다.',
      },
      {
        q: '위치 정보는 왜 필요한가요? / 로그인이 안 돼요.',
        a: '위치 정보는 같은 지역을 여행하는 동행 추천과 지역 오픈채팅에 사용되며, 기기 설정에서 언제든 권한을 관리할 수 있습니다. 카카오·구글 로그인 오류는 대부분 일시적이니 앱을 재시작해 보시고, 반복되면 가입 이메일과 함께 아래 문의 폼(분류 “계정·로그인”)으로 알려주세요.',
      },
    ],
  },

  dallime: {
    serviceName: 'dallime',
    serviceNameKo: '달리고',
    heroSubtitle:
      '자주 묻는 질문을 먼저 확인해 보시고, 해결되지 않는 내용은 아래 문의 폼으로 보내 주세요. 영업일 기준 1~2일 안에 답변드립니다.',
    contactEmail: 'flowto.ai.master@gmail.com',
    inquiryCategories: [
      '일반 문의',
      '운동·측정',
      '계정·로그인',
      '결제·환불',
      '데이터·개인정보',
      '기타',
    ],
    // 약관·정책 본문은 Supabase(legal_policies, app='dallime')에서 불러온다. (SupportLegal.js)
    termsUrl: '/support/dallime/terms',
    privacyUrl: '/support/dallime/privacy',
    // Google Play 데이터 삭제 요건용
    //   데이터 삭제:  https://flowto-ai.com/support/dallime#delete-data
    //   계정 삭제:    https://flowto-ai.com/support/dallime#delete-account
    dataDeletion: {
      developerName: 'Flowto.AI',
      account: {
        steps: [
          '이 페이지 하단 “문의 보내기”에서 문의 분류를 “계정·로그인”으로 선택합니다. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
          '문의 내용에 “계정 삭제(탈퇴) 요청”과 가입에 사용한 이메일(구글 계정)을 적어 보냅니다.',
          '본인 확인 후 영업일 기준 1~2일 안에 계정과 관련 데이터를 삭제하고, 처리 완료를 회신드립니다.',
        ],
        deleted:
          '프로필(닉네임 등), 이메일 및 계정 식별자, 캐릭터 성장·호감도·재화·배지·스토리 진행 데이터, 운동 기록(거리·시간·경로)과 위치·걸음 데이터, AI 캐릭터 대화 및 대화 맥락, 앱 이용 로그',
        retained: '결제 등 관련 법령상 보관 의무가 있는 최소한의 기록',
        retentionPeriod:
          '법정 보관 의무 기간이 지난 뒤 파기 (의무 없는 데이터는 요청 처리 시 즉시 파기)',
      },
      data: {
        steps: [
          '이 페이지 하단 “문의 보내기”에서 문의 분류를 “데이터·개인정보”로 선택합니다. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
          '삭제를 원하는 데이터 항목(예: 운동 기록, AI 대화 내역 등)과 가입 이메일을 적어 보냅니다.',
          '본인 확인 후 영업일 기준 1~2일 안에 해당 데이터를 삭제하고, 처리 완료를 회신드립니다.',
        ],
        deleted: '요청하신 특정 데이터 항목 (운동 기록, 위치·걸음 데이터, AI 대화 내역 등)',
        retained: '결제 등 관련 법령상 보관 의무가 있는 정보',
        retentionPeriod: '법정 보관 의무 기간이 지난 뒤 파기 (의무 없는 데이터는 즉시 파기)',
      },
    },
    webhookSource: 'support-dallime',
    faq: [
      {
        q: '달리고는 어떤 서비스인가요?',
        a: '달리고(dallime)는 걷기·달리기 등 운동 활동으로 좋아하는 AI 캐릭터를 성장시키는 연애 시뮬레이션형 운동 앱입니다. 운동한 만큼 캐릭터와의 호감도·스토리가 쌓이고, 캐릭터와 대화하며 꾸준히 운동할 동기를 얻을 수 있어요.',
      },
      {
        q: '걸음 수나 달린 거리가 측정되지 않아요.',
        a: '운동 측정에는 위치(GPS)와 신체 활동(걸음 수·활동 인식) 권한이 필요합니다. 기기 설정에서 위치 권한과 신체 활동/피트니스 권한을 허용해 주세요. 화면을 끄고 달릴 때도 기록되도록 위치 권한은 “앱 사용 중 허용” 이상으로 설정하고, 배터리 절약 최적화에서 앱을 제외하면 백그라운드 측정이 안정적입니다.',
      },
      {
        q: '화면을 끄면 기록이 멈춰요 / 백그라운드 추적이 안 돼요.',
        a: '달리고는 화면이 꺼진 상태에서도 위치를 추적하도록 만들어져 있습니다. 기기의 배터리 최적화·절전 모드에서 앱을 예외로 설정하고, 위치 권한을 허용하면 화면을 꺼도 경로가 계속 기록됩니다. 그래도 멈춘다면 기기 모델과 함께 아래 문의 폼(분류 “운동·측정”)으로 알려주세요.',
      },
      {
        q: 'AI 캐릭터와의 대화는 어떻게 처리되나요?',
        a: '캐릭터의 응답은 인공지능 모델이 생성하며, 자연스러운 대화와 장기 기억을 위해 대화 내용이 AI 사업자(Anthropic·OpenAI)를 통해 처리됩니다. AI 응답은 사실과 다를 수 있으니 참고용으로 이용해 주세요. 수집·처리에 관한 자세한 내용은 개인정보처리방침을 확인해 주세요.',
      },
      {
        q: '로그인이 안 돼요.',
        a: '구글 로그인 오류는 대부분 일시적이니 앱을 재시작하거나 네트워크를 확인해 보세요. 반복되면 가입에 사용한 구글 이메일과 함께 아래 문의 폼(분류 “계정·로그인”)으로 알려주시면 확인해 드립니다.',
      },
      {
        q: '유료 재화를 결제했는데 반영되지 않았어요 / 환불하고 싶어요.',
        a: '결제·환불은 앱 마켓(App Store·Google Play)의 정책에 따릅니다. 결제 후 재화가 반영되지 않았다면 앱을 재시작해 보시고, 그래도 반영되지 않으면 결제 영수증과 함께 아래 문의 폼(분류 “결제·환불”)으로 알려주세요.',
      },
      {
        q: '내 개인정보와 운동 데이터는 어떻게 보호되나요?',
        a: '서비스 제공에 필요한 최소한의 정보(구글 계정 이메일·프로필, 위치·걸음 등 운동 데이터, AI 대화, 기기 정보)를 수집하며, 모든 통신은 암호화된 채널로 전송·저장됩니다. 수집·이용·보관에 대한 자세한 내용은 개인정보처리방침을 확인해 주세요.',
      },
      {
        q: '계정을 삭제(탈퇴)하거나 내 데이터를 지우고 싶어요.',
        a: '이 페이지의 “데이터 및 계정 삭제 요청” 섹션에서 단계·삭제되는 데이터·보관 기간을 확인하고 요청하실 수 있습니다. 간단히는 아래 “문의 보내기”에서 분류 “계정·로그인”(탈퇴) 또는 “데이터·개인정보”(특정 데이터 삭제)를 선택해 요청하시면 영업일 기준 1~2일 안에 처리해 드립니다.',
      },
    ],
  },

  jamkkan: {
    serviceName: 'Jamkkan Eottae',
    serviceNameKo: '잠깐어때',
    heroSubtitle:
      '자주 묻는 질문을 먼저 확인해 보시고, 해결되지 않는 내용은 아래 문의 폼으로 보내 주세요. 영업일 기준 1~2일 안에 답변드립니다.',
    contactEmail: 'flowto.ai.master@gmail.com',
    inquiryCategories: [
      '일반 문의',
      '계정·로그인',
      '위치·코스',
      '데이터·개인정보',
      '기타',
    ],
    // 약관·정책 본문은 Supabase(legal_policies, app='jamkkan')에서 불러온다. (SupportLegal.js)
    termsUrl: '/support/jamkkan/terms',
    privacyUrl: '/support/jamkkan/privacy',
    // Google Play 데이터 삭제 요건용
    //   데이터 삭제:  https://flowto-ai.com/support/jamkkan#delete-data
    //   계정 삭제:    https://flowto-ai.com/support/jamkkan#delete-account
    dataDeletion: {
      developerName: 'FlowTo',
      account: {
        steps: [
          '앱에서 설정 > 계정 > 계정 삭제로 직접 삭제할 수 있습니다.',
          '앱을 쓸 수 없는 경우, 이 페이지 하단 “문의 보내기”에서 분류를 “계정·로그인”으로 선택해 “계정 삭제(탈퇴) 요청”과 로그인에 사용한 구글 계정 이메일을 적어 보냅니다. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
          '본인 확인 후 영업일 기준 1~2일 안에 계정과 관련 데이터를 삭제하고 회신드립니다.',
        ],
        deleted:
          '구글 계정 식별자(구글 sub), 계정에 저장된 코스 기록·완주 기록 (진행 중인 코스가 있으면 함께 종료). 기기 식별값은 앱 삭제 시 기기에서 사라집니다.',
        retained: '관련 법령상 보관 의무가 있는 최소한의 기록',
        retentionPeriod:
          '계정 삭제 시 관련 기록을 즉시 파기하며, 법령상 보관 의무가 있는 정보는 그 기간 경과 후 파기',
      },
      data: {
        steps: [
          '이 페이지 하단 “문의 보내기”에서 분류를 “데이터·개인정보”로 선택합니다. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
          '삭제를 원하는 데이터 항목(예: 특정 코스·완주 기록)과 로그인에 사용한 구글 계정 이메일을 적어 보냅니다.',
          '본인 확인 후 영업일 기준 1~2일 안에 해당 데이터를 삭제하고 회신드립니다.',
        ],
        deleted: '요청하신 특정 데이터 항목 (코스 기록, 완주 기록 등)',
        retained: '법령상 보관 의무가 있는 정보',
        retentionPeriod: '법정 보관 의무 기간 경과 후 파기 (의무 없는 데이터는 즉시 파기)',
      },
    },
    webhookSource: 'support-jamkkan',
    faq: [
      {
        q: '잠깐어때는 어떤 서비스인가요?',
        a: '잠깐어때는 지금 내 위치와 “쓸 수 있는 시간”을 입력하면, 그 시간 안에 주변 관광지를 둘러보고 출발지로 돌아오는 이동 코스를 추천하고 진행을 안내해 주는 앱입니다. 남은 시간·다음 목적지·복귀 시점을 실시간으로 알려줘서, 짧게 비는 시간에도 부담 없이 근처를 즐길 수 있어요.',
      },
      {
        q: '계정 없이도 쓸 수 있나요? 로그인하면 뭐가 달라지나요?',
        a: '네, 계정 없이(게스트) 모든 기능을 이용할 수 있습니다. 다만 게스트는 코스·완주 기록이 저장되지 않아 히스토리를 볼 수 없어요. 구글 로그인을 하면 기기를 바꿔도 지난 코스와 완주 기록을 이어서 볼 수 있습니다. (구글 로그인 시에도 계정 식별자만 저장하며 이름·이메일·프로필 사진은 저장하지 않습니다.)',
      },
      {
        q: '위치 권한을 꼭 켜야 하나요?',
        a: '위치 권한 없이도 이용할 수 있어요. 이 경우 기본 위치(서울시청)를 기준으로 코스가 계산되며 화면에 그 사실을 안내합니다. 정확한 추천을 위해서는 위치 권한을 허용하는 것이 좋고, 권한은 기기 설정에서 언제든지 끌 수 있습니다. 참고로 위치는 코스를 요청하는 시점의 좌표만 사용하며, 이동 경로를 계속 추적·저장하지 않습니다.',
      },
      {
        q: '지난 코스나 완주 기록은 어디서 보나요?',
        a: '구글 로그인 후 설정 > 계정 > 히스토리에서 지난 코스와 완주 기록을 확인할 수 있습니다. 게스트로 이용한 기록은 저장되지 않으니, 기록을 남기고 싶다면 구글 로그인을 이용해 주세요.',
      },
      {
        q: '안내하는 소요 시간이나 복귀 시점이 실제와 달라요.',
        a: '안내되는 시간은 예상치이며, 실제 이동 시간은 교통 상황·신호·날씨·개인 이동 속도에 따라 달라질 수 있습니다. 안내 시간을 지키려고 무리하게 이동하지 마시고, 이동 중에는 화면보다 주변 교통 상황과 신호를 먼저 확인해 주세요. 안전에 대한 책임은 이용자 본인에게 있습니다.',
      },
      {
        q: '관광지 정보(운영시간·휴무 등)가 실제와 달라요.',
        a: '관광지 정보는 한국관광공사 TourAPI 등 공공데이터를 기반으로 하며, 지도는 오픈스트리트맵과 국토교통부 공간정보 오픈플랫폼(VWorld)을 사용합니다. 현장 사정에 따라 실제와 다를 수 있으니 방문 전 해당 시설에 직접 확인하시길 권장합니다.',
      },
      {
        q: '어떤 개인정보를 수집하나요?',
        a: '서비스 동작에 필요한 최소한의 정보(앱이 자체 생성한 임의의 기기 식별값, 코스 발급 시점의 출발지·도착지 좌표, 코스·완주 기록)만 수집합니다. 구글 로그인 시 계정 식별자(sub) 1개가 추가됩니다. 이름·이메일·전화번호·프로필 사진은 저장하지 않으며, 광고·분석 SDK도 넣지 않았습니다. 자세한 내용은 개인정보처리방침을 확인해 주세요.',
      },
      {
        q: '구글 로그인이 안 돼요.',
        a: '대부분 일시적인 문제이니 앱을 재시작하거나 네트워크를 확인한 뒤 다시 시도해 주세요. 계속 실패하면 아래 “문의 보내기”에서 분류 “계정·로그인”을 선택해 증상과 사용 기기(안드로이드 버전 등)를 알려주시면 확인해 드립니다.',
      },
      {
        q: '계정을 삭제(탈퇴)하거나 내 데이터를 지우고 싶어요.',
        a: '앱에서 설정 > 계정 > 계정 삭제로 직접 삭제할 수 있으며, 삭제 시 계정에 저장된 코스·완주 기록은 즉시 삭제되고 복구할 수 없습니다. 앱을 쓸 수 없다면 이 페이지의 “데이터 및 계정 삭제 요청” 섹션을 참고하거나, 아래 “문의 보내기”에서 분류 “계정·로그인”(탈퇴)·“데이터·개인정보”(특정 데이터 삭제)를 선택해 요청해 주세요.',
      },
      {
        q: '만 14세 미만도 이용할 수 있나요?',
        a: '회사는 만 14세 미만 아동의 개인정보를 수집하지 않습니다. 만 14세 미만 아동이 법정대리인의 동의 없이 이용한 사실이 확인되면 지체 없이 해당 정보를 삭제합니다.',
      },
    ],
  },
  marketfit: {
    serviceName: 'MarketFit Tester',
    serviceNameKo: '마켓핏 테스터',
    heroSubtitle:
      '자주 묻는 질문을 먼저 확인해 보시고, 해결되지 않는 내용은 아래 문의 폼으로 보내 주세요. 영업일 기준 1~2일 안에 답변드립니다.',
    contactEmail: 'flowto.ai.master@gmail.com',
    inquiryCategories: [
      '일반 문의',
      '조사·리포트',
      '계정·로그인',
      '결제·환불',
      '데이터·개인정보',
      '기타',
    ],
    // 약관·정책 본문은 Supabase(legal_policies, app='marketfit')에서 불러온다. (SupportLegal.js)
    termsUrl: '/support/marketfit/terms',
    privacyUrl: '/support/marketfit/privacy',
    dataDeletion: {
      developerName: 'Flowto.AI',
      account: {
        steps: [
          '서비스 내 설정 화면에서 회원 탈퇴를 진행할 수 있습니다.',
          '설정 화면을 이용할 수 없는 경우, 이 페이지 하단 “문의 보내기”에서 분류를 “계정·로그인”으로 선택해 “계정 삭제(탈퇴) 요청”과 로그인에 사용한 이메일(구글 또는 카카오 계정)을 적어 보냅니다. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
          '본인 확인 후 영업일 기준 1~2일 안에 계정과 관련 데이터를 삭제하고 회신드립니다.',
        ],
        deleted:
          '이메일 주소, 이름 또는 닉네임, 소셜 로그인 제공자가 발급한 회원 식별자, 회원이 입력한 사업 아이템 설명·업종·지역·조사 설정값, 첨부한 참고 자료, 조사 실행 기록과 생성된 리포트, 잔여 이용 권한. 알림 수신을 신청한 경우 수집된 휴대전화번호도 함께 삭제됩니다.',
        retained:
          '전자상거래 등에서의 소비자보호에 관한 법률에 따른 계약·청약철회 기록과 대금결제·재화 공급 기록(각 5년), 소비자 불만·분쟁처리 기록(3년), 표시·광고 기록(6개월), 통신비밀보호법에 따른 서비스 접속 기록(3개월). 부정 이용 방지를 위해 해지 회원의 식별 정보는 해지일로부터 6개월간 분리 보관합니다.',
        retentionPeriod:
          '탈퇴 시 지체 없이 파기하며, 위 법령상 보관 의무가 있는 정보는 각 보관 기간이 지난 후 파기합니다.',
      },
      data: {
        steps: [
          '이 페이지 하단 “문의 보내기”에서 분류를 “데이터·개인정보”로 선택합니다. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
          '삭제를 원하는 데이터 항목(예: 특정 아이템의 조사 기록·리포트)과 로그인에 사용한 이메일을 적어 보냅니다.',
          '본인 확인 후 영업일 기준 1~2일 안에 해당 데이터를 삭제하고 회신드립니다.',
        ],
        deleted:
          '요청하신 특정 데이터 항목 (입력한 아이템 내용, 첨부 자료, 조사 기록, 생성된 리포트 등)',
        retained: '법령상 보관 의무가 있는 결제·거래 기록 등',
        retentionPeriod: '법정 보관 의무 기간 경과 후 파기 (의무 없는 데이터는 즉시 파기)',
      },
    },
    webhookSource: 'support-marketfit',
    faq: [
      {
        q: '마켓핏 테스터는 어떤 서비스인가요?',
        a: '사업 아이템을 입력하면 업종·지역 설정에 맞춘 가상의 손님(페르소나)에게 모의 조사를 돌려, 반응과 상세 분석을 리포트로 만들어 주는 서비스입니다. 개선안을 반영해 같은 아이템을 다시 조사할 수도 있고, 리포트는 문서로 내려받을 수 있습니다.',
      },
      {
        q: '조사 결과를 그대로 믿어도 되나요?',
        a: '아니요. 조사 결과는 인공지능이 생성한 가상의 손님 반응을 바탕으로 한 참고 자료이며, 실제 시장의 성과나 매출을 보장하지 않습니다. 의사결정의 근거 중 하나로만 활용해 주시고, 중요한 판단은 실제 고객·전문가 확인을 함께 거치시길 권장합니다.',
      },
      {
        q: '무료로도 써볼 수 있나요?',
        a: '계정당 통산 1회에 한해 무료 조사를 제공합니다. 무료 조사는 가상 손님 수와 리포트 항목이 유료 상품보다 제한되며, 매월 다시 지급되지는 않습니다.',
      },
      {
        q: '유료 상품에는 어떤 종류가 있나요?',
        a: '조사 1회와 상세 분석을 이용하는 “검증 1회권”, 이미 실행한 조사에 개선안을 반영해 다시 조사할 때 쓰는 “추가 검증권”, 결제일로부터 30일 동안 정해진 횟수만큼 이용하는 “이용권”이 있습니다. 가격과 포함 횟수는 요금 안내 화면에 표시되며 표시 가격은 부가가치세를 포함한 금액입니다.',
      },
      {
        q: '결제하면 언제부터 쓸 수 있나요?',
        a: '모든 유료 상품은 별도의 배송이 없는 온라인 디지털 콘텐츠입니다. 결제가 승인되면 즉시 계정에 반영되어 로그인 후 바로 이용할 수 있습니다.',
      },
      {
        q: '이용권은 자동으로 갱신되나요?',
        a: '자동 갱신되지 않습니다. 이용권은 결제일로부터 30일이 지나면 종료되며, 계속 이용하시려면 만료 후 직접 다시 결제해 주셔야 합니다. 이용권에 포함된 횟수 중 사용하지 않은 부분은 기간 만료와 함께 소멸하고 다음 기간으로 이월되지 않습니다. 다만 별도로 구매한 검증 1회권·추가 검증권의 잔량은 이용권이 만료되어도 소멸하지 않습니다.',
      },
      {
        q: '검증권에 유효기간이 있나요?',
        a: '검증 1회권과 추가 검증권은 부여일로부터 60일간 유효하며, 기간이 지나면 남은 잔량은 소멸합니다.',
      },
      {
        q: '환불은 어떤 경우에 받을 수 있나요?',
        a: '구매일로부터 7일 이내이고 아직 이용을 시작하지 않았다면 전액 환불됩니다. 다만 전자상거래 등에서의 소비자보호에 관한 법률에 따라, 검증 1회권·추가 검증권으로 조사가 실행되어 분석이 시작된 시점부터, 그리고 이용권으로 해당 기간 중 첫 조사 또는 다시 조사가 실행된 시점부터는 청약철회가 제한됩니다. 상품이 표시·광고와 다르게 제공된 경우에는 공급받은 날부터 3개월 이내, 그 사실을 안 날부터 30일 이내에 철회하실 수 있습니다.',
      },
      {
        q: '리포트는 언제까지 볼 수 있나요?',
        a: '조사로 생성된 리포트의 열람과 PDF 다운로드는 조사 완료일로부터 90일 동안 보장됩니다. 오래 보관하실 자료는 기간 내에 내려받아 두시길 권장합니다.',
      },
      {
        q: '세금계산서나 지출 증빙을 받을 수 있나요?',
        a: '카드 결제는 카드사 매출전표로 지출 증빙이 됩니다. 세금계산서가 필요하시면 아래 “문의 보내기”에서 분류를 “결제·환불”로 선택해 사업자등록증과 함께 요청해 주세요.',
      },
      {
        q: '내가 입력한 아이템 내용이 AI 학습에 쓰이나요?',
        a: '아니요. 조사 실행 시 입력하신 아이템 설명과 참고 자료는 조사 응답을 생성하기 위해 인공지능 처리 사업자(OpenAI, Anthropic, Google, xAI)에 전송되지만, 회사는 해당 입력이 이들 사업자의 모델 학습에 이용되지 않도록 학습 미사용 조건이 적용되는 방식으로 서비스를 이용하고 있습니다. 자세한 내용은 개인정보처리방침 제6조를 확인해 주세요.',
      },
      {
        q: '휴대전화번호는 꼭 입력해야 하나요?',
        a: '아니요. 휴대전화번호는 조사 완료·이용권 만료 알림 수신을 신청하신 경우에만 수집하는 선택 항목이며, 제공하지 않으셔도 서비스 이용에는 제한이 없습니다.',
      },
      {
        q: '비밀번호는 어디에 저장되나요?',
        a: '회사는 비밀번호를 직접 수집하거나 보관하지 않습니다. 회원 인증은 구글 또는 카카오 소셜 로그인으로만 이루어지며, 카드번호 등 결제수단 정보도 결제대행사가 직접 처리하고 회사는 보관하지 않습니다.',
      },
      {
        q: '만 14세 미만도 이용할 수 있나요?',
        a: '회사는 만 14세 미만 아동의 개인정보를 수집하지 않습니다. 만 14세 미만인 분은 서비스를 이용하실 수 없습니다.',
      },
      {
        q: '계정을 삭제(탈퇴)하거나 내 데이터를 지우고 싶어요.',
        a: '서비스 내 설정 화면에서 탈퇴할 수 있으며, 이 페이지의 “데이터 및 계정 삭제 요청” 섹션에 절차를 정리해 두었습니다. 계정은 유지하면서 특정 아이템·리포트만 지우고 싶으시면 아래 “문의 보내기”에서 분류를 “데이터·개인정보”로 선택해 요청해 주세요.',
      },
    ],
  },

  pangdoku: {
    serviceName: 'Bbangdoku',
    serviceNameKo: '빵도쿠',
    heroSubtitle:
      '자주 묻는 질문을 먼저 확인해 보시고, 해결되지 않는 내용은 아래 문의 폼으로 보내 주세요. 영업일 기준 1~2일 안에 답변드립니다.',
    contactEmail: 'flowto.ai.master@gmail.com',
    inquiryCategories: [
      '일반 문의',
      '게임 진행·버그',
      '광고',
      '데이터·개인정보',
      '기타',
    ],
    // 약관·정책 본문은 Supabase(legal_policies, app='pangdoku')에서 불러온다. (SupportLegal.js)
    termsUrl: '/support/pangdoku/terms',
    privacyUrl: '/support/pangdoku/privacy',
    // Google Play 데이터 삭제 요건용
    //   데이터 삭제:  https://flowto-ai.com/support/pangdoku#delete-data
    //   계정 삭제:    https://flowto-ai.com/support/pangdoku#delete-account
    dataDeletion: {
      developerName: 'Flowto.AI',
      account: {
        steps: [
          '빵도쿠는 회원가입이 없어 삭제할 계정 자체가 없습니다.',
          '게임 데이터(진행도·보유 아이템·획득한 프로필 이미지·설정)는 이용자의 기기에만 저장되므로, 앱을 삭제하면 함께 사라집니다.',
          '그 밖에 확인이 필요한 사항은 이 페이지 하단 “문의 보내기”에서 분류를 “데이터·개인정보”로 선택해 문의해 주세요. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
        ],
        deleted:
          '기기에 저장된 게임 데이터 (진행도, 보유 아이템, 획득한 프로필 이미지, 설정) — 앱 삭제 시 기기에서 함께 삭제됩니다.',
        retained: '회사가 별도로 보관하는 계정 정보가 없습니다.',
        retentionPeriod: '앱 삭제 즉시 기기에서 제거됩니다.',
      },
      data: {
        steps: [
          '기기 저장 데이터는 앱 내 설정에서 초기화하거나 앱을 삭제하면 제거됩니다.',
          '광고 식별자 기반 맞춤 광고를 원하지 않으시면 기기 설정(안드로이드: 설정 > 개인정보 보호 > 광고, iOS: 설정 > 개인정보 보호 및 보안 > 추적)에서 끄실 수 있습니다.',
          '추가로 확인이 필요하면 이 페이지 하단 “문의 보내기”에서 분류를 “데이터·개인정보”로 선택해 요청해 주세요.',
        ],
        deleted: '기기에 저장된 게임 데이터 및 설정값',
        retained: '법령상 보관 의무가 있는 정보',
        retentionPeriod: '법정 보관 의무 기간 경과 후 파기 (의무 없는 데이터는 즉시 파기)',
      },
    },
    webhookSource: 'support-pangdoku',
    faq: [
      {
        q: '빵도쿠는 어떤 게임인가요?',
        a: '색 영역과 행·열마다 빵을 하나씩 놓는 1인용 논리 퍼즐 게임입니다. 회원가입 없이 무료로 이용할 수 있으며 광고를 통해 운영됩니다.',
      },
      {
        q: '회원가입이나 로그인이 필요한가요?',
        a: '필요 없습니다. 앱을 설치하면 바로 이용할 수 있고, 회사는 이름·이메일 같은 계정 정보를 수집하지 않습니다.',
      },
      {
        q: '기기를 바꾸면 진행도가 이어지나요?',
        a: '진행도·보유 아이템·설정 등 게임 데이터는 이용자의 기기에만 저장됩니다. 따라서 기기를 바꾸거나 앱을 삭제하면 데이터가 이어지지 않고 사라집니다.',
      },
      {
        q: '데일리 도전은 어떤 기준으로 바뀌나요?',
        a: 'UTC 날짜를 기준으로 하루에 한 판씩 공통 퍼즐이 제공됩니다. 거주 지역의 시각과 갱신 시점이 다르게 느껴질 수 있습니다.',
      },
      {
        q: '힌트 같은 아이템은 어떻게 얻나요?',
        a: '힌트·위치 보기 등 아이템은 게임 진행을 돕는 게임 내 수단입니다. 획득 조건은 앱 내 안내를 확인해 주세요.',
      },
      {
        q: '광고를 끄거나 맞춤 광고를 거부할 수 있나요?',
        a: '맞춤형 광고는 기기 설정에서 거부하실 수 있습니다. 안드로이드는 설정 > 개인정보 보호 > 광고, iOS는 설정 > 개인정보 보호 및 보안 > 추적에서 조정할 수 있습니다. 거부하셔도 광고 자체는 표시되며, 관심사 기반 맞춤만 중단됩니다.',
      },
      {
        q: '퍼즐이 풀리지 않거나 게임이 멈춰요.',
        a: '앱을 완전히 종료한 뒤 다시 실행해 보시고, 스토어에서 최신 버전으로 업데이트되어 있는지 확인해 주세요. 그래도 계속되면 아래 “문의 보내기”에서 분류를 “게임 진행·버그”로 선택해 증상과 사용 기기·앱 버전을 알려주시면 확인해 드립니다.',
      },
      {
        q: '어떤 개인정보를 수집하나요?',
        a: '회원가입이 없어 계정 정보를 수집하지 않으며, 게임 데이터는 기기에 저장됩니다. 광고 게재와 이용 통계를 위해 광고 식별자 등 자동 수집 정보가 처리될 수 있으며, 자세한 내용은 개인정보처리방침을 확인해 주세요.',
      },
      {
        q: '내 데이터를 지우고 싶어요.',
        a: '게임 데이터는 기기에만 저장되므로, 앱 내 설정에서 초기화하거나 앱을 삭제하면 함께 제거됩니다. 이 페이지의 “데이터 및 계정 삭제 요청” 섹션에 절차를 정리해 두었습니다.',
      },
    ],
  },
  arrows: {
    serviceName: 'Arrows Puzzle World Tour',
    serviceNameKo: '애로우즈 퍼즐 월드 투어',
    heroSubtitle:
      '자주 묻는 질문을 먼저 확인해 보시고, 해결되지 않는 내용은 아래 문의 폼으로 보내 주세요. 영업일 기준 1~2일 안에 답변드립니다.',
    contactEmail: 'flowto.ai.master@gmail.com',
    inquiryCategories: ['일반 문의', '게임 진행·버그', '광고', '데이터·개인정보', '기타'],
    // 약관·정책 본문은 Supabase(legal_policies, app='arrows')에서 불러온다. (SupportLegal.js)
    // 원문 정본: FlowTo-ai/arrows_puzzle docs/legal/privacy.md · terms.md
    termsUrl: '/support/arrows/terms',
    privacyUrl: '/support/arrows/privacy',
    // Google Play 데이터 삭제 요건용
    //   데이터 삭제:  https://flowto-ai.com/support/arrows#delete-data
    //   계정 삭제:    https://flowto-ai.com/support/arrows#delete-account
    dataDeletion: {
      developerName: 'Flowto.AI',
      account: {
        steps: [
          '애로우즈 퍼즐 월드 투어는 회원가입이 없어 삭제할 계정 자체가 없습니다.',
          '게임 데이터(여행 진행도·앨범·설정)는 이용자의 기기에만 저장되므로, 앱을 삭제하면 함께 사라집니다.',
          '그 밖에 확인이 필요한 사항은 이 페이지 하단 “문의 보내기”에서 분류를 “데이터·개인정보”로 선택해 문의해 주세요. (또는 flowto.ai.master@gmail.com 으로 직접 메일)',
        ],
        deleted:
          '기기에 저장된 게임 데이터 (여행 진행도, 앨범, 설정) — 앱 삭제 시 기기에서 함께 삭제됩니다.',
        retained: '회사가 별도로 보관하는 계정 정보가 없습니다.',
        retentionPeriod: '앱 삭제 즉시 기기에서 제거됩니다.',
      },
      data: {
        steps: [
          '앱 내 설정 > 데이터 초기화를 실행하면 여행 진행도와 앨범이 처음 상태로 돌아갑니다. (소리·진동 설정은 유지되며, 앱을 삭제하면 모두 제거됩니다.)',
          '광고 식별자 기반 맞춤 광고를 원하지 않으시면 기기 설정(안드로이드: 설정 > 개인정보 보호 > 광고, iOS: 설정 > 개인정보 보호 및 보안 > 추적)에서 끄거나 광고 식별자를 삭제하실 수 있습니다.',
          '이용 통계 삭제 등 추가 요청은 이 페이지 하단 “문의 보내기”에서 분류를 “데이터·개인정보”로 선택해 보내 주세요.',
        ],
        deleted: '기기에 저장된 여행 진행도와 앨범',
        retained: '개인을 식별할 수 없는 게임 이용 통계 (Google 애널리틱스)',
        retentionPeriod: '이용 통계는 수집일로부터 최대 14개월 보관 후 자동 삭제',
      },
    },
    webhookSource: 'support-arrows',
    faq: [
      {
        q: '애로우즈 퍼즐 월드 투어는 어떤 게임인가요?',
        a: '저마다 나갈 방향을 가진 선을 순서대로 빼내 길을 여는 1인용 퍼즐 게임입니다. 다섯 레벨마다 관문을 넘으면 그 도시의 풍경이 앨범에 남습니다. 회원가입 없이 무료로 이용할 수 있으며 광고를 통해 운영됩니다.',
      },
      {
        q: '회원가입이나 로그인이 필요한가요?',
        a: '필요 없습니다. 앱을 설치하면 바로 이용할 수 있고, 회사는 이름·이메일 같은 계정 정보를 수집하지 않습니다.',
      },
      {
        q: '기기를 바꾸면 진행도가 이어지나요?',
        a: '여행 진행도·앨범·설정 등 게임 데이터는 이용자의 기기에만 저장됩니다. 따라서 기기를 바꾸거나 앱을 삭제하면 데이터가 이어지지 않고 사라집니다.',
      },
      {
        q: '하트가 모두 줄어들면 어떻게 되나요?',
        a: '앞이 막힌 선을 누르면 하트가 하나 줄어듭니다. 하트를 모두 쓰면 그 판을 처음부터 다시 시작할 수 있고, 하트도 다시 채워집니다.',
      },
      {
        q: '힌트와 지우개는 어떻게 쓰나요?',
        a: '힌트는 지금 빼낼 수 있는 선을 알려 주고, 지우개는 3레벨부터 쓸 수 있습니다. 둘 다 판마다 정해진 횟수가 주어지며, 판을 다시 시작하거나 다음 레벨로 넘어가면 새로 채워집니다.',
      },
      {
        q: '광고를 끄거나 맞춤 광고를 거부할 수 있나요?',
        a: '맞춤형 광고는 기기 설정에서 거부하실 수 있습니다. 안드로이드는 설정 > 개인정보 보호 > 광고, iOS는 설정 > 개인정보 보호 및 보안 > 추적에서 조정할 수 있습니다. 거부하셔도 광고 자체는 표시되며, 관심사 기반 맞춤만 중단됩니다.',
      },
      {
        q: '퍼즐이 풀리지 않거나 게임이 멈춰요.',
        a: '앱을 완전히 종료한 뒤 다시 실행해 보시고, 스토어에서 최신 버전으로 업데이트되어 있는지 확인해 주세요. 그래도 계속되면 아래 “문의 보내기”에서 분류를 “게임 진행·버그”로 선택해 증상과 레벨 번호·사용 기기·앱 버전을 알려주시면 확인해 드립니다.',
      },
      {
        q: '어떤 정보를 수집하나요?',
        a: '회원가입이 없어 계정 정보를 수집하지 않으며, 게임 데이터는 기기에 저장됩니다. 광고 게재(Google AdMob)와 이용 통계 분석(Google 애널리틱스)을 위해 광고 식별자·앱 인스턴스 ID·기기 정보 등 자동 수집 정보가 처리되며, 자세한 내용은 개인정보처리방침을 확인해 주세요.',
      },
      {
        q: '내 데이터를 지우고 싶어요.',
        a: '앱 내 설정 > 데이터 초기화로 여행 진행도와 앨범을 지울 수 있고, 앱을 삭제하면 모든 게임 데이터가 함께 제거됩니다. 이 페이지의 “데이터 및 계정 삭제 요청” 섹션에 절차를 정리해 두었습니다.',
      },
    ],
  },
};

export default supportConfig;
